import { Link } from 'react-router-dom';
import { CelebrityPost } from '../../../mocks/celebritySaju';

interface CelebrityCardProps {
  post: CelebrityPost;
  featured?: boolean;
}

const categoryColors: Record<string, string> = {
  '기업인': 'bg-amber-500/20 text-amber-300 border-amber-500/30',
  '정치인': 'bg-red-500/20 text-red-300 border-red-500/30',
  '연예인': 'bg-pink-500/20 text-pink-300 border-pink-500/30',
  '방송인': 'bg-orange-500/20 text-orange-300 border-orange-500/30',
  '스포츠': 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
};

export default function CelebrityCard({ post, featured = false }: CelebrityCardProps) {
  const categoryColor = categoryColors[post.category] || 'bg-purple-500/20 text-purple-300 border-purple-500/30';

  if (featured) {
    return (
      <Link to={`/celebrity-saju/${post.slug}`} className="block group">
        <article className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#1a0f2e]/80 to-[#0f0a1e]/90 border border-white/10 hover:border-primary-gold/40 transition-all duration-500 cursor-pointer">
          {/* Large image */}
          <div className="relative w-full h-72 overflow-hidden">
            <img
              src={post.thumbnail}
              alt={post.name}
              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0f0a1e] via-[#0f0a1e]/40 to-transparent"></div>
            {/* Featured badge */}
            <div className="absolute top-4 left-4 px-3 py-1 rounded-full bg-primary-gold/90 text-black text-xs font-bold tracking-wider">
              FEATURED
            </div>
            <div className={`absolute top-4 right-4 px-2.5 py-1 rounded-full border text-xs font-semibold ${categoryColor}`}>
              {post.category}
            </div>
          </div>

          <div className="p-6">
            {/* Name */}
            <div className="mb-3">
              <h2 className="text-2xl font-bold text-white group-hover:text-primary-gold transition-colors duration-300"
                style={{ fontFamily: '"Noto Serif KR", serif' }}>
                {post.name}
              </h2>
              <p className="text-purple-400 text-sm mt-0.5">{post.nameEn} · {post.birthDate}</p>
            </div>

            <p className="text-purple-200/80 text-sm leading-relaxed mb-4 line-clamp-2">{post.summary}</p>

            {/* Analysis badges */}
            <div className="flex flex-wrap gap-2 mb-4">
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-white/5 border border-white/10">
                <span className="text-primary-gold text-xs">日干</span>
                <span className="text-white text-xs font-semibold">{post.dayMaster}</span>
              </div>
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-white/5 border border-white/10">
                <span className="text-purple-300 text-xs">태양궁</span>
                <span className="text-white text-xs font-semibold">{post.astrology.sun}</span>
              </div>
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-white/5 border border-white/10">
                <span className="text-amber-400 text-xs">자미</span>
                <span className="text-white text-xs font-semibold">{post.ziwei.mainStar}</span>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex gap-1.5">
                {post.tags.slice(0, 3).map((tag) => (
                  <span key={tag} className="text-xs text-purple-400/70 bg-purple-500/10 px-2 py-0.5 rounded-full">#{tag}</span>
                ))}
              </div>
              <span className="text-xs text-purple-500">{post.readTime}분 읽기</span>
            </div>
          </div>
        </article>
      </Link>
    );
  }

  return (
    <Link to={`/celebrity-saju/${post.slug}`} className="block group">
      <article className="flex gap-4 p-4 rounded-xl bg-gradient-to-br from-[#1a0f2e]/60 to-[#0f0a1e]/80 border border-white/8 hover:border-primary-gold/30 transition-all duration-400 cursor-pointer">
        {/* Thumbnail */}
        <div className="relative w-28 h-20 flex-shrink-0 rounded-xl overflow-hidden">
          <img
            src={post.thumbnail}
            alt={post.name}
            className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0f0a1e]/30"></div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1.5">
            <h3 className="text-base font-bold text-white group-hover:text-primary-gold transition-colors duration-300 truncate"
              style={{ fontFamily: '"Noto Serif KR", serif' }}>
              {post.name}
            </h3>
            <span className={`flex-shrink-0 px-2 py-0.5 rounded-full border text-xs font-semibold ${categoryColor}`}>
              {post.category}
            </span>
          </div>
          <p className="text-purple-300/60 text-xs mb-2">{post.birthDate} · {post.dayMaster}</p>
          <p className="text-purple-200/70 text-xs leading-relaxed line-clamp-2">{post.summary}</p>
        </div>
      </article>
    </Link>
  );
}
